/**
 * Created by neo on 23.12.22.
 */
import * as React from 'react';
import { Suspense } from 'react';
import { observer } from 'mobx-react';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Container } from 'reactstrap';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Theme } from '../../Theme/Theme';
import { ExploreCategorySwitch } from '../../Components/ExploreCards/ExploreCategorySwitch';
import { NutritionLibrarySection } from './NutritionLibrarySection';
import { useExploreCategoriesSuspense } from '../../Hooks/useExploreCategoriesSuspense';
import { InitialCategoriesSkeleton } from '../../Components/Skeletons/InitialCategoriesSkeleton';
import { ServiceLifecycleCard } from '../../Components/ServiceLifecycleBoundary';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ScreenContainer = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
  background: ${Theme.Colors.white};
`;

const HeroSection = styled.div`
  background: white;
  padding: 32px 0 24px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const AccentText = styled.span`
  color: #00dda2;
`;

const HeroSubtitle = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
  max-width: 600px;

  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

const CategoriesSection = styled.div`
  background: ${Theme.Colors.white};
  padding: 32px 0 24px;
`;

const FinalSection = styled.div`
  padding: 48px 0;
  background: white;
`;

export type NutritionIndexScreenProps = {};

const NutritionCategories: React.FC = observer(() => {
  const { categories } = useExploreCategoriesSuspense('nutrition');

  return (
    <>
      {categories.map((category, index) => (
        <ExploreCategorySwitch
          key={category.id}
          category={category}
          categoryIndex={index}
          isLast={index === categories.length - 1}
          linkPrefix="/nutrition/"
        />
      ))}
    </>
  );
});

export const NutritionIndexScreen: React.FC<NutritionIndexScreenProps> = observer(() => {
  const { t } = useTrans();

  return (
    <ScreenContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <AccentText>{t('nutrition.hero.accent', { defaultValue: 'Healthy' })}</AccentText>{' '}
            {t('nutrition.hero.titleStart', { defaultValue: 'Recipes & Nutrition' })}
          </HeroTitle>
          <HeroSubtitle>
            {t('nutrition.hero.subtitle', {
              defaultValue: 'Discover delicious recipes, nutrition tips, and healthy meal ideas tailored for you.',
            })}
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <ServiceLifecycleCard />

      <CategoriesSection>
        <Suspense fallback={<InitialCategoriesSkeleton />}>
          <NutritionCategories />
        </Suspense>
      </CategoriesSection>

      <FinalSection>
        <Container>
          <NutritionLibrarySection />
        </Container>
      </FinalSection>
    </ScreenContainer>
  );
});
